import { useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  type Discipline,
  type PlannedWorkout,
  type WorkoutStatus,
} from '@/lib/workouts';

/**
 * Tarjeta atómica para un workout planificado dentro de la cuadrícula
 * semanal del dashboard.
 *
 * Decisiones:
 * - El borde izquierdo color-codifica el estado (completed/skipped/partial)
 *   sin depender de iconos, manteniéndolo accesible para daltonismo (los
 *   propios botones tienen icono+aria-label).
 * - Los tres botones de marcado (✓ ⊘ ◐) son chicos pero con `aria-label`
 *   completo: "Marcar 30 min de carrera como completado". `aria-pressed`
 *   refleja el estado actual.
 * - El cuerpo de la descripción es click-to-expand: clave en mobile donde no
 *   hay hover.
 */
export function WorkoutCard({
  workout,
  onMark,
  isPending = false,
}: {
  workout: PlannedWorkout;
  onMark: (id: number, status: WorkoutStatus) => void;
  isPending?: boolean;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const desc = workout.description ?? '';
  const isLong = desc.length > 100;
  const shown = !expanded && isLong ? `${desc.slice(0, 100).trimEnd()}…` : desc;

  const disciplineEs = disciplineLabel(workout.discipline);
  const durMin = Math.round(workout.durationPlannedMin);
  const ariaContext = `${durMin} min de ${disciplineEs}`;

  return (
    <article
      className={`rounded-md border-l-4 ${statusBorderClass(workout.status)} ${
        workout.status === 'skipped' ? 'opacity-60' : ''
      } bg-white p-2 text-xs shadow-sm`}
      aria-label={`Entrenamiento de ${disciplineEs}, ${durMin} minutos, estado ${statusLabel(workout.status)}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-900">
          {formatTime(workout.datetime, workout.timezone)}
        </span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
          {durMin} min
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${disciplineBadgeClass(
            workout.discipline,
          )}`}
        >
          {disciplineEs}
        </span>
        {workout.intensityZone && (
          <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-medium text-slate-50">
            {workout.intensityZone}
          </span>
        )}
      </div>

      {desc && (
        <div className="mt-1.5 text-[11px] leading-snug text-slate-700">
          <button
            type="button"
            onClick={() => isLong && setExpanded((v) => !v)}
            className={`block w-full text-left ${isLong ? 'cursor-pointer hover:text-slate-900' : 'cursor-default'}`}
            aria-expanded={isLong ? expanded : undefined}
            aria-label={isLong ? (expanded ? 'Contraer descripción' : 'Expandir descripción') : undefined}
          >
            <p className="whitespace-pre-wrap">{shown}</p>
          </button>
        </div>
      )}

      {workout.actualWorkoutId !== null && (
        <Link
          to="/entreno/$id"
          params={{ id: String(workout.actualWorkoutId) }}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 hover:text-teal-900 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          aria-label={`Ver detalle del entreno real vinculado a ${ariaContext}`}
        >
          <span aria-hidden="true">→</span> Ver detalle
        </Link>
      )}

      <div className="mt-2 flex items-center gap-1" role="group" aria-label={`Marcar ${ariaContext}`}>
        <MarkButton
          glyph="✓"
          status="completed"
          currentStatus={workout.status}
          onMark={(s) => onMark(workout.id, s)}
          ariaLabel={`Marcar ${ariaContext} como completado`}
          activeClass="bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-600"
          disabled={isPending}
        />
        <MarkButton
          glyph="◐"
          status="partial"
          currentStatus={workout.status}
          onMark={(s) => onMark(workout.id, s)}
          ariaLabel={`Marcar ${ariaContext} como parcial`}
          activeClass="bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500"
          disabled={isPending}
        />
        <MarkButton
          glyph="⊘"
          status="skipped"
          currentStatus={workout.status}
          onMark={(s) => onMark(workout.id, s)}
          ariaLabel={`Marcar ${ariaContext} como saltado`}
          activeClass="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
          disabled={isPending}
        />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Mark button
// ---------------------------------------------------------------------------

function MarkButton({
  glyph,
  status,
  currentStatus,
  onMark,
  ariaLabel,
  activeClass,
  disabled,
}: {
  glyph: string;
  status: WorkoutStatus;
  currentStatus: WorkoutStatus;
  onMark: (s: WorkoutStatus) => void;
  ariaLabel: string;
  activeClass: string;
  disabled?: boolean;
}): ReactElement {
  const active = currentStatus === status;
  return (
    <button
      type="button"
      onClick={() => onMark(active ? 'planned' : status)}
      aria-label={ariaLabel}
      aria-pressed={active}
      disabled={disabled}
      className={`inline-flex h-6 w-6 items-center justify-center rounded text-[11px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        active
          ? activeClass
          : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 focus:ring-slate-500'
      }`}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers de display
// ---------------------------------------------------------------------------

export function formatTime(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function disciplineLabel(d: Discipline): string {
  switch (d) {
    case 'swim': return 'Natación';
    case 'bike': return 'Ciclismo';
    case 'run': return 'Carrera';
    case 'strength': return 'Fuerza';
    case 'brick': return 'Brick';
    case 'other': return 'Otro';
  }
}

/**
 * Clases Tailwind con contraste AA. Compartidas con `PlanPreview` para
 * mantener consistencia visual.
 */
export function disciplineBadgeClass(d: Discipline): string {
  switch (d) {
    case 'swim': return 'bg-cyan-100 text-cyan-900';
    case 'bike': return 'bg-orange-100 text-orange-900';
    case 'run': return 'bg-emerald-100 text-emerald-900';
    case 'strength': return 'bg-slate-200 text-slate-800';
    case 'brick': return 'bg-purple-100 text-purple-900';
    case 'other': return 'bg-slate-100 text-slate-700';
  }
}

export function statusBorderClass(s: WorkoutStatus): string {
  switch (s) {
    case 'completed': return 'border-emerald-500';
    case 'skipped': return 'border-rose-500';
    case 'partial': return 'border-amber-500';
    case 'planned': return 'border-slate-300';
  }
}

export function statusLabel(s: WorkoutStatus): string {
  switch (s) {
    case 'completed': return 'completado';
    case 'skipped': return 'saltado';
    case 'partial': return 'parcial';
    case 'planned': return 'planificado';
  }
}
